import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Clock, X } from 'lucide-react';

// Tempo de inatividade antes de expirar (30 minutos)
const SESSION_TIMEOUT = 30 * 60 * 1000;
// Tempo antes de expirar para mostrar aviso (5 minutos)
const WARNING_TIME = 5 * 60 * 1000;
// Intervalo de verificação (30 segundos)
const CHECK_INTERVAL = 30 * 1000;

export default function SessionManager({ isAuthenticated, onLogout, children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Atualizar última atividade
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
    setRemainingTime(null);
  }, []);

  // Estender sessão
  const extendSession = useCallback(() => {
    updateActivity();
    toast.success('Sessão estendida por mais 30 minutos!');
  }, [updateActivity]);

  // Monitorar atividade do usuário
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      // Só atualiza se não estiver mostrando o aviso
      if (!showWarning) {
        setLastActivity(Date.now());
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, showWarning]);

  // Verificar timeout periodicamente
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = () => {
      const now = Date.now();
      const elapsed = now - lastActivity;
      const remaining = SESSION_TIMEOUT - elapsed;

      if (remaining <= 0) {
        // Sessão expirou
        setShowWarning(false);
        toast.error('Sessão expirada por inatividade. Faça login novamente.');
        onLogout();
      } else if (remaining <= WARNING_TIME && !showWarning) {
        // Mostrar aviso
        setShowWarning(true);
        setRemainingTime(Math.ceil(remaining / 1000));
      } else if (remaining > WARNING_TIME && showWarning) {
        // Atividade detectada, esconder aviso
        setShowWarning(false);
        setRemainingTime(null);
      }
    };

    const interval = setInterval(checkSession, CHECK_INTERVAL);
    checkSession(); // Verificar imediatamente

    return () => clearInterval(interval);
  }, [isAuthenticated, lastActivity, showWarning, onLogout]);

  // Atualizar tempo restante a cada segundo quando mostrando aviso
  useEffect(() => {
    if (!showWarning || !isAuthenticated) return;

    const updateRemaining = () => {
      const now = Date.now();
      const elapsed = now - lastActivity;
      const remaining = SESSION_TIMEOUT - elapsed;
      
      if (remaining <= 0) {
        onLogout();
      } else {
        setRemainingTime(Math.ceil(remaining / 1000));
      }
    };

    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [showWarning, isAuthenticated, lastActivity, onLogout]);

  // Formatar tempo
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      
      {/* Banner de Aviso de Sessão Expirando */}
      {showWarning && isAuthenticated && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 shadow-lg z-[9999] animate-slide-up"
          data-testid="session-warning-banner"
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm sm:text-base">
                  Sua sessão vai expirar em breve!
                </p>
                <p className="text-xs sm:text-sm opacity-90">
                  Por segurança, você será desconectado por inatividade.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Timer */}
              <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                <Clock className="w-4 h-4" />
                <span className="font-mono font-bold text-lg">
                  {remainingTime ? formatTime(remainingTime) : '--:--'}
                </span>
              </div>
              
              {/* Botão Continuar */}
              <button
                onClick={extendSession}
                className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-orange-50 transition-colors shadow-md"
                data-testid="extend-session-button"
              >
                Continuar usando
              </button>
              
              {/* Botão Fechar */}
              <button
                onClick={() => setShowWarning(false)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Fechar aviso"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos de animação */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
}
